export type RoomKey =
  | 'exterior'
  | 'kitchen'
  | 'primary_bed'
  | 'primary_bath'
  | 'living'
  | 'basement_mechanical'
  | 'whole_home'
  | 'other';

export interface RoomTip {
  key: RoomKey;
  label: string;
  emoji: string;
  doNow: string[];
  watchFor?: string[];
}

export const ROOM_ORDER: RoomKey[] = [
  'exterior',
  'kitchen',
  'primary_bed',
  'primary_bath',
  'living',
  'basement_mechanical',
  'whole_home',
  'other',
];

export const ROOM_TIPS: Record<RoomKey, RoomTip> = {
  exterior: {
    key: 'exterior',
    label: 'Outside',
    emoji: '🌳',
    doNow: [
      'Stand at the curb — look at the roofline for sags or dips.',
      'Walk the perimeter — check if ground slopes away from the foundation.',
      'Look up at gutters and downspouts — attached, clear, draining away from the house?',
    ],
    watchFor: [
      'Cracks in foundation or exterior brick (especially horizontal).',
      'HVAC unit age plate.',
      'Tree branches touching the roof.',
      'Standing water in beds.',
    ],
  },
  kitchen: {
    key: 'kitchen',
    label: 'Kitchen',
    emoji: '🍳',
    doNow: [
      'Turn the sink on hot — note pressure and how long until it gets hot.',
      'Open the cabinet under the sink — feel for damp, look for staining.',
      'Look up at the ceiling — any stains under an upstairs bath?',
    ],
    watchFor: [
      'GFCI outlets near water.',
      'Dishwasher seal.',
      'Soft-close cabinets working.',
      'Range vent actually pulling air.',
    ],
  },
  primary_bed: {
    key: 'primary_bed',
    label: 'Primary bed',
    emoji: '🛏',
    doNow: [
      'Walk in — does the room feel the size you expected?',
      'Stand at the window — what do you hear, and what do you look at?',
      'Open and close the door — does it stick or rub the frame?',
    ],
    watchFor: [
      'Closet size vs. your wardrobe.',
      'Outlet count.',
      'New paint on one wall (sometimes covers patches).',
    ],
  },
  primary_bath: {
    key: 'primary_bath',
    label: 'Primary bath',
    emoji: '🛁',
    doNow: [
      'Run the shower for 30 seconds — does the drain keep up?',
      'Flush the toilet — listen for it refilling cleanly.',
      'Look at grout and caulk lines — discoloration, cracks, or missing sections?',
    ],
    watchFor: [
      'Exhaust fan actually pulls (hold a tissue).',
      'Soft floor near tub or toilet.',
      'Under-sink dampness.',
    ],
  },
  living: {
    key: 'living',
    label: 'Living areas',
    emoji: '🛋',
    doNow: [
      'Walk the floor — feel for soft spots or slope (drop a coin if unsure).',
      'Look up — any ceiling stains, especially in corners?',
      'Open and close a window — smooth, sealed, locks engage?',
    ],
    watchFor: [
      'Cracks radiating from window/door corners.',
      'Fresh paint on one ceiling section.',
      'Outlet count vs. how you would actually live here.',
    ],
  },
  basement_mechanical: {
    key: 'basement_mechanical',
    label: 'Basement / mechanical',
    emoji: '🏚',
    doNow: [
      'Smell test — musty = moisture history.',
      'Look at foundation walls for hairline cracks, white powder (efflorescence), or watermarks.',
      'Check the water heater and HVAC for rust, pooling, or an age plate older than 15 years.',
    ],
    watchFor: [
      'Sump pump present and clean?',
      'Joists overhead — any stains or sister-boards?',
      'Electrical panel — modern breakers or old fuses?',
    ],
  },
  whole_home: {
    key: 'whole_home',
    label: 'Whole home',
    emoji: '🏠',
    doNow: [
      'Stop and listen for 30 seconds — traffic, planes, neighbors, HVAC.',
      'Look at the natural light right now — is this the time of day you would actually be home?',
      'Pretend you live here — where do you drop your keys, where do you eat, where do kids/pets go?',
    ],
    watchFor: [
      'Lingering odors (pet, cigarette, mildew) hidden under air freshener.',
      'Fresh paint everywhere (sometimes a tell).',
      'Layout flow vs. your actual life.',
    ],
  },
  other: {
    key: 'other',
    label: 'Other rooms',
    emoji: '🚪',
    doNow: [
      'Note the room name.',
      'Snap one photo if it matters.',
      'One-line reaction.',
    ],
  },
};

export const ROOM_FOOTER =
  'Your inspector will do the deep dive — you are just flagging what is worth a second look.';
